import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ClearOutlined from "@mui/icons-material/ClearOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

// One entry per public marketing URL on www.pharmacyhalo.com.au. The PHLinks
// service 302s each slug to the value stored here (tblPharmacyHaloSettings);
// clearing a value falls back to the listed default.
const FIELDS = [
  {
    key: "WebsiteRedirect_PHBanner",
    publicUrl: "https://www.pharmacyhalo.com.au/PHBanner",
    label: "/PHBanner target",
    helper:
      "Where the pull-up banner QR code sends visitors. Clear to use the default booking page (https://www.pharmacyhalo.com.au/book/). Changes go live within a minute.",
  },
  {
    key: "WebsiteRedirect_Conference",
    publicUrl: "https://www.pharmacyhalo.com.au/Conference",
    label: "/Conference target",
    helper:
      "Where the conference/event QR code sends visitors. Clear to use the default booking page (https://www.pharmacyhalo.com.au/book/). Changes go live within a minute.",
  },
];

const ALL_FIELDS = FIELDS.map((f) => f.key);

const emptyValues = () => Object.fromEntries(ALL_FIELDS.map((k) => [k, ""]));

// Absolute http(s) URL or a site-relative path — mirrors the API's validation.
const isValidTarget = (v) => v === "" || /^https?:\/\/\S+$/.test(v) || /^\/\S*$/.test(v);

const WebsiteRedirectsModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(emptyValues());
  const [values, setValues] = useState(emptyValues());
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.WEBSITE_REDIRECTS, "GET");
      const settings = res?.settings ?? {};
      const next = emptyValues();
      ALL_FIELDS.forEach((key) => { next[key] = settings[key] ?? ""; });
      setOriginal(next);
      setValues(next);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load website redirect settings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    } else {
      setError(null);
      setOriginal(emptyValues());
      setValues(emptyValues());
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleClear = (key) => {
    setValues((prev) => ({ ...prev, [key]: "" }));
  };

  const isDirty = ALL_FIELDS.some((key) => (values[key] || "").trim() !== (original[key] || "").trim());
  const allValid = ALL_FIELDS.every((key) => isValidTarget((values[key] || "").trim()));

  const handleSave = async () => {
    const changed = {};
    ALL_FIELDS.forEach((key) => {
      if ((values[key] || "").trim() !== (original[key] || "").trim()) {
        changed[key] = (values[key] || "").trim();
      }
    });
    if (Object.keys(changed).length === 0) return;

    setSaving(true);
    try {
      const res = await phappsRequest(API_PATHS.WEBSITE_REDIRECTS, "PUT", changed);
      const settings = res?.settings ?? {};
      const next = emptyValues();
      ALL_FIELDS.forEach((key) => { next[key] = settings[key] ?? ""; });
      setOriginal(next);
      setValues(next);
      setSnackbar({ open: true, message: "Website redirects updated ✓", severity: "success" });
      onClose();
    } catch (e) {
      setSnackbar({ open: true, message: getAdminApiErrorMessage(e, "Failed to save website redirects"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${BLUE}15` }}>
              <PublicOutlined sx={{ fontSize: 19, color: BLUE }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#1e293b", lineHeight: 1.2 }}>
                Website Redirects
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Where marketing QR links on www.pharmacyhalo.com.au point
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close" disabled={saving}>
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={26} />
            </Box>
          ) : error ? (
            <Alert severity="warning">{error}</Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {FIELDS.map((field) => {
                const value = values[field.key] ?? "";
                const invalid = !isValidTarget(value.trim());
                return (
                  <Box
                    key={field.key}
                    sx={{ backgroundColor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 2, p: 2 }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", color: BLUE, textTransform: "uppercase", letterSpacing: 0.5, mb: 1.25 }}>
                      {field.publicUrl}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                      <TextField
                        label={field.label}
                        helperText={invalid ? "Must be a full http(s) URL or a path starting with /" : field.helper}
                        error={invalid}
                        value={value}
                        onChange={handleChange(field.key)}
                        placeholder="https://www.pharmacyhalo.com.au/book/"
                        fullWidth
                        size="small"
                        variant="outlined"
                        // White field on the grey card so the input area stays clear.
                        sx={{ backgroundColor: "#fff", borderRadius: 1 }}
                      />
                      <Tooltip title="Clear (use default)">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleClear(field.key)}
                            disabled={!value}
                            sx={{ mt: 0.5 }}
                          >
                            <ClearOutlined sx={{ fontSize: 17 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving || !!error || !isDirty || !allValid}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: BLUE, "&:hover": { bgcolor: "#001a52" } }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WebsiteRedirectsModal;
